import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";

import { useStepEmployerStore } from "#src/stores/step-employer.ts";
import { useStepContactStore } from "#src/stores/step-contact.ts";
import { type StoreGeneric, type Pinia } from "pinia";
import { computed } from "vue";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    address: required(REQS.INSURED_ADDRESS),
    yearsAtAddress: required(REQS.INSURED_ADDRESS_START_DATE),
  };
}

interface AddressState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

interface AddressStep extends BaseStepStore, ToSetupState<AddressState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): AddressState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializeAddressStep(pinia: Pinia): AddressStep {
  const state = generateInitialState<AddressState>(initialState());

  function $reset() {
    resetState<AddressState>(state, initialState());
  }

  const step = computed(() => STEP.ADDRESS);
  const previous = computed(() => {
    const contact = useStepContactStore(pinia);
    return contact.step;
  });
  const next = computed(() => {
    const employer = useStepEmployerStore(pinia);
    if (employer.inactive) return employer.next;
    return employer.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
    state.inactive.value = !Object.values(state.requiredFields.value).includes(
      true
    );
  }

  return {
    ...state,
    $reset,
    step,
    previous,
    next,
    setStepRequirements,
  };
}

export const useStepAddressStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, AddressStep>("step-address", () => {
    return initializeAddressStep(pinia);
  })(pinia, hot);
};
